<script setup lang="ts" generic="Value extends MultiselectDropdownMenuValue = MultiselectDropdownMenuValue">
import { useResizeObserver, watchDebounced } from "@vueuse/core";
import { Menu } from "floating-vue";
import { type Component, computed, inject, nextTick, provide, ref, shallowRef, watch } from "vue";
import type { ComponentExposed } from "vue-component-type-helpers";

import MultiselectDropdownInner from "~/components/dumb/MultiselectDropdownInner.vue";
import GlobalPopper, { PopperType } from "~/components/globalPopper";
import MobileBottomSheet from "~/components/MobileBottomSheet.vue";
import {
  DROPDOWN_NESTING_LEVEL,
  DROPDOWN_PARENT_ON_AFTER_CLOSE_FUNCTION,
  DROPDOWN_PARENT_ON_AFTER_OPEN_FUNCTION,
  DROPDOWN_ROOT_REFERENCE_NODE_GETTER,
} from "~/constants/injection";
import { colorsByTheme } from "~/constants/style";
import { Placement, UserRole } from "~/shared/enums";
import type {
  MultiselectDropdownMenuItem,
  MultiselectDropdownMenuSection,
  MultiselectDropdownMenuValue,
  NoOptionAction,
  ValidationFunctionResult,
} from "~/shared/types";
import { usePageStore, useUserStore } from "~/stores";

const openMobileSheets = new Set<() => void>();

defineOptions({ inheritAttrs: false });

const props = defineProps<{
  container?: string;
  disabled?: boolean;
  sections: MultiselectDropdownMenuSection<Value>[];
  placement?: Placement;
  headerTitle?: string;
  placeholder?: string;
  emptyText?: string;
  newEntryIcon?: Component;
  newEntryIconArgs?: object;
  makeNewEntryText?: (search: string) => string;
  newEntryRoleOverride?: UserRole;
  inline?: boolean;
  block?: boolean;
  heightBlock?: boolean;
  distance?: number;
  skidding?: number;
  cover?: boolean;
  widthPixels?: number;
  minItemSize?: number;
  maxItems?: number;
  closeOnSelect?: boolean;
  dontPropagateClose?: boolean;
  itemClasses?: string[];
  propagateClickClasses?: string[];
  infiniteList?: boolean;
  loadingMore?: boolean;
  autoSize?: boolean;
  enableNewEntry?: boolean;
  popperClass?: string;
  showAll?: boolean;
  showMoreForSingleSection?: boolean;
  skipNestingLevel?: boolean;
  validate?: (value: string) => ValidationFunctionResult;
  noOptionActions?: NoOptionAction | NoOptionAction[];
  onKeydown?: (event: KeyboardEvent, search: string) => void;
  onAfterOpen?: () => void;
  onAfterClose?: () => void;
  hasNewEntrySlot?: boolean;
}>();

const emit = defineEmits<{
  add: [value: Value];
  create: [value: string];
  remove: [value: Value];
  replace: [value: Value];
  search: [value: string];
}>();

const pageStore = usePageStore();
const userStore = useUserStore();

const colors = computed(() => colorsByTheme[pageStore.theme]);
const isMobile = computed(() => pageStore.isMobile);

const contentRef = shallowRef<ComponentExposed<typeof MultiselectDropdownInner<Value>> | null>(null);
const search = ref("");
const scrollContainer = computed(() => contentRef.value?.scrollContainer ?? null);
const mobileSheet = ref<ComponentExposed<typeof MobileBottomSheet<boolean>> | null>(null);

const currentSearch = computed(() => (isMobile.value ? (mobileSheet.value?.search ?? "") : search.value));

const newEntryValidationResult = computed(() =>
  props.enableNewEntry ? (props.validate?.(currentSearch.value) ?? { isValid: true, error: undefined }) : undefined
);
const newEntryIsValid = computed(() => newEntryValidationResult.value?.isValid);
const newEntryError = computed(() =>
  newEntryValidationResult.value && !newEntryValidationResult.value.isValid
    ? newEntryValidationResult.value.error
    : undefined
);

const placementDefinite = computed(() => props.placement ?? Placement.BOTTOM_LEFT);
const defaultDistance = computed(() => props.distance ?? (props.cover ? 0 : -8));
const calcDistance = ref(defaultDistance.value);

const clearSearch = () => {
  search.value = "";
  if (!isMobile.value) {
    contentRef.value?.clearSearch();
  } else {
    mobileSheet.value?.clearSearch();
  }
};

const filterItems = (items: MultiselectDropdownMenuItem<Value>[]) => {
  const searchLower = currentSearch.value.toLowerCase();

  const itemsToFilter = isMobile.value ? items : items.filter((item) => !item.selected || item.alwaysShowInList);

  let results = currentSearch.value
    ? itemsToFilter.filter(
        (item) =>
          item.label.toLowerCase().includes(searchLower) ||
          item.adtlSearchTerms?.some((term) => term && term.toLowerCase().includes(searchLower))
      )
    : itemsToFilter;

  if (isMobile.value) {
    results = results.toSorted((a, b) => {
      if (a.selected && !b.selected) {
        return -1;
      }
      if (!a.selected && b.selected) {
        return 1;
      }
      return 0;
    });
  }

  return results.slice(0, Number(props.maxItems ?? results.length)).map((item, index) => ({ ...item, index }));
};

const items = computed(() => props.sections.flatMap((section) => section.items));

const selectedItems = computed(() => items.value.filter((item) => item.selected));
const filteredSections = computed(() =>
  props.sections.map((section) => ({ ...section, items: filterItems(section.items) }))
);

const newEntryAllowed = computed(() => userStore.isRoleGte(props.newEntryRoleOverride ?? UserRole.ADMIN));

const contentProps = computed(() => ({
  filteredSections: filteredSections.value,
  items: items.value,
  search: isMobile.value ? mobileSheet.value?.search : search.value,
  placeholder: props.placeholder,
  emptyText: props.emptyText,
  itemClasses: props.itemClasses,
  enableNewEntry: props.enableNewEntry,
  newEntryIsValid: newEntryIsValid.value,
  newEntryError: newEntryError.value,
  newEntryAllowed: newEntryAllowed.value,
  newEntryIcon: props.newEntryIcon,
  newEntryIconArgs: props.newEntryIconArgs,
  makeNewEntryText: props.makeNewEntryText,
  hasNewEntrySlot: props.hasNewEntrySlot,
  infiniteList: props.infiniteList,
  loadingMore: props.loadingMore,
}));

const dropdownRef = ref<InstanceType<typeof GlobalPopper> | null>(null);
const wrapperRef = computed(() => dropdownRef.value?.triggerRef);
const recalculateDistance = () => {
  calcDistance.value = defaultDistance.value;
  if (!wrapperRef.value || !props.cover) {
    return;
  }
  calcDistance.value -= wrapperRef.value.getBoundingClientRect().height;
};
useResizeObserver(wrapperRef, recalculateDistance);
watch(() => props.cover, recalculateDistance);
watch(defaultDistance, recalculateDistance);
watch(wrapperRef, recalculateDistance, { once: true });

const isOpen = ref(false);
const isHidden = ref(false);

const parentOnAfterOpen = inject(DROPDOWN_PARENT_ON_AFTER_OPEN_FUNCTION);
const parentOnAfterClose = inject(DROPDOWN_PARENT_ON_AFTER_CLOSE_FUNCTION);

const parentNestingLevel = inject(DROPDOWN_NESTING_LEVEL, 0);
const currentNestingLevel = parentNestingLevel + 1;
provide(DROPDOWN_NESTING_LEVEL, currentNestingLevel);

const computedReferenceNode = computed(() => {
  if (props.skipNestingLevel) {
    return undefined;
  }
  if (currentNestingLevel > 2) {
    const rootReferenceNodeGetter = inject(DROPDOWN_ROOT_REFERENCE_NODE_GETTER);
    if (rootReferenceNodeGetter) {
      return rootReferenceNodeGetter();
    }
  }
  return undefined;
});

provide(DROPDOWN_PARENT_ON_AFTER_OPEN_FUNCTION, () => {
  if (!isMobile.value) {
    return;
  }
  isHidden.value = true;
});
provide(DROPDOWN_PARENT_ON_AFTER_CLOSE_FUNCTION, () => {
  if (!isMobile.value) {
    return;
  }
  isHidden.value = false;
});

const close = () => {
  if (isMobile.value) {
    isOpen.value = false;
    contentRef.value?.clearExpandedSections();
    contentRef.value?.clearFocus();
    props.onAfterClose?.();
    parentOnAfterClose?.();
  } else {
    dropdownRef.value?.hide();
  }
};

const runNoOptionAction = (action: NoOptionAction) => {
  action.onAction(currentSearch.value);
  clearSearch();
  if (!isMobile.value) {
    contentRef.value?.focusSearch();
  }
  if (props.closeOnSelect || action.closeOnAction) {
    close();
  }
};

const resolvedNoOptionActions = computed(() => {
  if (!props.noOptionActions) {
    return [];
  }
  const raw = Array.isArray(props.noOptionActions) ? props.noOptionActions : [props.noOptionActions];
  return raw.map((action) => {
    const validation = action.validate?.(currentSearch.value) ?? { isValid: true, error: undefined };
    return {
      icon: action.icon,
      iconArgs: action.iconArgs,
      text: action.makeText(currentSearch.value),
      isValid: validation.isValid,
      error: validation.isValid ? undefined : validation.error,
      allowed: userStore.isRoleGte(action.roleOverride ?? UserRole.ADMIN),
      onAction: () => runNoOptionAction(action),
    };
  });
});

const onSelect = (item: MultiselectDropdownMenuItem<Value>) => {
  if (item.disabled) {
    return;
  }

  if (item.selected) {
    emit("remove", item.value);
  } else {
    emit("add", item.value);
  }
  if (!isMobile.value) {
    contentRef.value?.focusSearch();
  }
  if (props.closeOnSelect) {
    close();
  }
};

const onCreate = () => {
  if (!props.enableNewEntry || !newEntryIsValid.value) {
    return;
  }

  emit("create", currentSearch.value);
  if (!props.hasNewEntrySlot) {
    clearSearch();
  }
  if (!isMobile.value) {
    contentRef.value?.focusSearch();
  }
  if (props.closeOnSelect) {
    close();
  }
};

// Emit remove event when user hits backspace with no search value
const onBackspace = (event: KeyboardEvent) => {
  if (search.value !== "") {
    return;
  }

  const lastSelectedItem = selectedItems.value[selectedItems.value.length - 1];
  if (!lastSelectedItem) {
    return;
  }

  emit("remove", lastSelectedItem.value);
  event.stopPropagation();
};

const moveUpOrDown = (isUp: boolean) => {
  if (isUp) {
    contentRef.value?.selectPreviousItem();
    return;
  }
  contentRef.value?.selectNextItem();
};

const onKeydown = (event: KeyboardEvent) => {
  if (!isOpen.value || isMobile.value) {
    return;
  }
  const { key } = event;

  props.onKeydown?.(event, search.value);

  switch (key) {
    case "Shift":
    case "Meta":
    case "Control":
    case "Option":
    case "Alt": {
      break;
    }
    case "Enter": {
      event.preventDefault();
      const focusedItem = contentRef.value?.focusedItem;
      if (focusedItem) {
        onSelect(focusedItem);
        break;
      }
      const focusedAction = contentRef.value?.focusedAction;
      if (focusedAction) {
        if (focusedAction.isValid) {
          focusedAction.onAction();
        }
        break;
      }
      if (contentRef.value?.isShowingActions) {
        const runnableActions = resolvedNoOptionActions.value.filter((action) => action.isValid && action.allowed);
        if (runnableActions.length === 1) {
          runnableActions[0].onAction();
        }
        break;
      }
      onCreate();
      break;
    }
    case "Escape": {
      event.preventDefault();
      close();
      break;
    }
    case "ArrowUp":
    case "ArrowDown": {
      moveUpOrDown(key === "ArrowUp");
      event.preventDefault();
      break;
    }
    case "Tab": {
      event.preventDefault();
      moveUpOrDown(event.shiftKey);
      break;
    }
    default: {
      if (document.activeElement?.tagName === "INPUT") {
        break;
      }
      contentRef.value?.focusSearch();
      break;
    }
  }
};

const updateSearch = (value: string) => {
  search.value = value;
  contentRef.value?.selectFirstItem();
};

watch(currentSearch, (value) => !props.infiniteList && emit("search", value));
watchDebounced(currentSearch, (value) => props.infiniteList && emit("search", value), { debounce: 500, maxWait: 1000 });

const onShow = () => {
  isOpen.value = true;
  search.value = "";
  props.onAfterOpen?.();
  parentOnAfterOpen?.();

  if (!isMobile.value) {
    nextTick(() => contentRef.value?.focusSearch());
  }
};

const onHide = () => {
  isOpen.value = false;
  contentRef.value?.clearExpandedSections();
  contentRef.value?.clearFocus();
  props.onAfterClose?.();
  parentOnAfterClose?.();
};

const open = () => {
  if (isMobile.value && !props.disabled) {
    openMobileSheets.forEach((closeFn) => closeFn());
    openMobileSheets.add(close);
    isOpen.value = true;
    isHidden.value = false;
    props.onAfterOpen?.();
    parentOnAfterOpen?.();
  } else {
    dropdownRef.value?.show();
  }
};

const closeAndPropagateMaybe = (event: MouseEvent) => {
  close();
  const { clientX, clientY } = event;
  props.propagateClickClasses
    ?.flatMap((e) => Array.from(document.getElementsByClassName(e)))
    .filter((e): e is HTMLElement => e instanceof HTMLElement)
    .forEach((elem) => {
      const { left, top, width, height } = elem.getBoundingClientRect();
      if (!(clientX >= left && clientX <= left + width && clientY >= top && clientY <= top + height)) {
        return;
      }
      elem.click();
    });
};

defineExpose({
  open,
  close,
  isOpen,
  scrollContainer,
});
</script>

<template>
  <GlobalPopper
    v-if="!isMobile"
    ref="dropdownRef"
    v-bind="$attrs"
    :type="PopperType.DROPDOWN"
    :container="container"
    :reference-node="() => computedReferenceNode"
    :disabled="disabled"
    :placement="placementDefinite"
    :distance="calcDistance"
    :skidding="skidding"
    :dont-propagate-close="dontPropagateClose"
    :auto-size="autoSize"
    no-auto-focus
    :theme="`dropdown-${pageStore.theme}`"
    :popper-class="popperClass"
    :class="{
      flex: !inline,
      inline: Boolean(inline),
      'max-w-full grow': block,
      'h-full': heightBlock,
      'cursor-pointer': !disabled,
    }"
    @apply-show="onShow"
    @apply-hide="onHide">
    <template #default>
      <slot />
    </template>
    <template #popper>
      <Menu no-auto-focus @mousedown.prevent>
        <MultiselectDropdownInner
          ref="contentRef"
          v-bind="contentProps"
          :no-option-actions="resolvedNoOptionActions"
          :block="props.block"
          :auto-size="props.autoSize"
          :width-pixels="props.widthPixels"
          :min-item-size="props.minItemSize"
          :colors="colors"
          :show-all="showAll"
          :show-more-for-single-section="showMoreForSingleSection"
          @select="onSelect"
          @remove="emit('remove', $event)"
          @create="onCreate"
          @replace="emit('replace', $event)"
          @update-search="updateSearch"
          @keydown="onKeydown"
          @backspace="onBackspace">
          <template #newEntrySlot>
            <slot name="newEntrySlot" />
          </template>
          <template #extra>
            <slot name="extra" :close="close" />
          </template>
        </MultiselectDropdownInner>
        <slot name="footer" :close="close" />
        <Teleport to="body">
          <div class="absolute inset-0" :class="popperClass" @click="closeAndPropagateMaybe" @keydown.escape="close" />
        </Teleport>
      </Menu>
    </template>
  </GlobalPopper>

  <template v-else>
    <div
      v-bind="$attrs"
      class="flex"
      :class="[
        $attrs.class,
        {
          inline: Boolean(inline),
          'max-w-full grow': block,
          'h-full': heightBlock,
          'cursor-pointer': !disabled,
        },
      ]"
      @click="open"
      @keydown="open">
      <slot />
    </div>

    <MobileBottomSheet
      v-if="isOpen"
      ref="mobileSheet"
      :entity="isOpen"
      :hidden="isHidden"
      :title="headerTitle"
      include-done
      include-search
      should-update-keyboard-height
      @close="close">
      <MultiselectDropdownInner
        ref="contentRef"
        v-bind="contentProps"
        :no-option-actions="resolvedNoOptionActions"
        :block="false"
        auto-size
        :width-pixels="undefined"
        :min-item-size="props.minItemSize"
        :colors="undefined"
        :show-all="showAll"
        :show-more-for-single-section="showMoreForSingleSection"
        @select="onSelect"
        @remove="emit('remove', $event)"
        @create="onCreate"
        @replace="emit('replace', $event)"
        @update-search="updateSearch"
        @keydown="onKeydown"
        @backspace="onBackspace">
        <template #newEntrySlot>
          <slot name="newEntrySlot" />
        </template>
        <template #extra>
          <slot name="extra" :close="close" />
        </template>
      </MultiselectDropdownInner>
      <slot name="footer" :close="close" />
    </MobileBottomSheet>
  </template>
</template>

<style>
@reference "#frontend.css";
.v-popper--theme-dropdown .v-popper__arrow-container {
  display: none;
}
.v-popper--theme-dropdown .v-popper__wrapper {
  box-shadow:
    0 10px 15px -3px rgb(0 0 0 / 0.1),
    0 4px 6px -4px rgb(0 0 0 / 0.1);
}
.v-popper--theme-dropdown .v-popper__inner {
  border-width: 0px;
  border-radius: 4px;
  background-color: transparent;
}
</style>
